import { Vec3 } from 'ayvri-std/lib/vector';
import { delerp } from './Math';
import { FocusCam } from './Camera';
import * as Ease from './Easing';
import Trigger from './Trigger';
export class Event {
    constructor(options) {
        this._active = false;
        this.options = options;
    }
    // Called when the trigger fires
    start() {
        this._active = true;
    }
    // May be manually called by an event to stop the event
    stop() {
        this._active = false;
    }
    // Called every tick while active is true
    update() { }
    // Called when the user has scrubbed back before the trigger fired
    undo() {
        this._active = false;
    }
    get active() {
        return this._active;
    }
}
export class FocusOnPhotoGroup extends Event {
    start(app) {
        super.start();
        // Parse options
        this.photos = this.options.photos;
        this.currentIdx = 0;
        // Pause the scene while the group is displayed
        app.store.dispatch({ type: 'PUSH_PAUSE' });
        this.groupCam = new FocusCam();
        app.camera.addCamera(this.groupCam);
        // Start the FocusEvent for the first photo
        this.focusNextPhoto(app);
    }
    stop(app) {
        app.store.dispatch({ type: 'POP_PAUSE' });
        this.groupCam.stop();
        super.stop();
    }
    focusNextPhoto(app) {
        const photo = this.photos[this.currentIdx];
        // Change the time so that the rest of the scene is in the correct state for this Photo
        app.store.dispatch({ type: 'SET_TIME', time: photo.targetTime });
        this.photoTime = photo.targetTime;
        photo.focusEvent.start(app, this.groupCam);
    }
    update(app) {
        const focusEvent = this.photos[this.currentIdx].focusEvent;
        // Tick the focus event for the current Photo
        focusEvent.update(app);
        // Stop if the user scrubs
        const sceneTime = app.store.getState().clock.time;
        if (sceneTime !== this.photoTime) {
            this.stop(app);
            return;
        }
        // Once focus has finished, move on to the next Photo (or stop)
        if (!focusEvent.active) {
            ++this.currentIdx;
            if (this.currentIdx < this.photos.length) {
                this.focusNextPhoto(app);
            }
            else {
                this.stop(app);
            }
        }
    }
    undo() {
        // Do nothing. Because the trigger is instantaneous, we want this event to continue ticking until we decide its done
    }
}
export class FocusOnPhoto extends Event {
    start(app, camera) {
        super.start();
        const state = app.store.getState();
        // Parse options
        const { photo, duration, screenExtent = 1.0 } = this.options;
        this.element = photo.photoElem;
        this.timerElem = photo.timerElem;
        this.timerRect = photo.timerRect;
        this.screenExtent = screenExtent;
        // Compute (real world) time at which the focus will stop
        this.startTime = new Date().getTime();
        this.stopTime = this.startTime + duration;
        // The FocusCamera is managed by our parent
        this.cam = camera;
        // Compute the center of the photo
        const height = this.element.worldPos.length();
        const scale = 1.0 + (this.element.worldHeight * 0.5) / height;
        this.centerPos = Vec3.multiply(this.element.worldPos, scale, new Vec3());
        // Pause the scene while the photo is displayed
        app.store.dispatch({ type: 'PUSH_PAUSE' });
        // If the user scrubs, cancel the photo
        this.photoTime = state.clock.time;
    }
    stop(app) {
        if (!this._active) {
            return;
        }
        this.element.setZScale(1.0);
        app.store.dispatch({ type: 'POP_PAUSE' });
        super.stop();
    }
    update(app) {
        const state = app.store.getState();
        // Bias the HUD element towards the camera so that it doesn't clip terrain
        const kZScaleDuration = 1000;
        const t = (new Date().getTime() - this.startTime) / kZScaleDuration;
        this.element.setZScale(Math.max(0.1, 1.0 - t));
        this.timerElem.setZScale(0.0);
        // Move the timer along
        const timerT = Math.min(1.0, delerp(this.startTime, this.stopTime, new Date().getTime()));
        this.timerElem.setScaleX(this.timerRect.w * timerT);
        // Compute the camera FOV in both dimensions
        const camera = app.camera.camera;
        const aspect = state.layout.width / state.layout.height;
        const fovx = aspect > 1.0 ? camera.fov : Math.atan(Math.tan(camera.fov * 0.5) * aspect) * 2.0;
        const fovy = aspect <= 1.0 ? camera.fov : Math.atan(Math.tan(camera.fov * 0.5) / aspect) * 2.0;
        // Compute the camera distance so that the image's smallest dimension nearly fills the screen
        const photoHeight = this.element.worldHeight;
        const photoWidth = (this.element.canvasWidth / this.element.canvasHeight) * photoHeight;
        const maxDist = Math.max(photoWidth / Math.tan(fovx * 0.5), photoHeight / Math.tan(fovy * 0.5));
        const distance = (maxDist * 0.5) / this.screenExtent;
        this.cam.setTarget(this.centerPos, distance);
        // Stop focusing when we timeout or the user scrubs
        const sceneTime = state.clock.time;
        const realTime = new Date().getTime();
        const scrubbed = sceneTime !== this.photoTime;
        const stopped = this.stopTime && realTime >= this.stopTime;
        if (stopped || scrubbed) {
            this.stop(app);
        }
    }
    undo() {
        // Do nothing. Because the trigger is instantaneous, we want this event to continue ticking until we decide its done
    }
}
// Transition between the photo and thumbnail HUD elements
export class ShowPhoto extends Event {
    start() {
        super.start();
        this.zeroTime = new Date().getTime();
        this.duration = this.options.inDuration;
    }
    update() {
        const kJumpHeightPx = 100;
        const kTimerTransitionDurationMs = 100;
        // T increases from 0->1 while transitioning in, and 1->0 while transitioning out
        const t = (new Date().getTime() - this.zeroTime) / this.duration;
        const timerT = (t * this.duration) / kTimerTransitionDurationMs;
        // Set HUD parameters based on t
        this.options.photo.photoElem.setShow(t > 0.0);
        this.options.photo.photoElem.setScale(Ease.outCubic(t));
        this.options.photo.photoElem.setOffset(0, -kJumpHeightPx * Ease.jumpInOutCubic(t, 0.2));
        this.options.photo.timerElem.setShow(t > 0.0);
        this.options.photo.timerElem.setOpacity(Math.min(1.0, timerT));
        // Stop updating once we've completely transitioned out
        if (t < 0.0)
            this.stop();
    }
    undo() {
        // Set the zero time in the future so that t will decrease
        this.zeroTime = new Date().getTime() + this.options.outDuration;
        this.duration = -this.options.outDuration;
    }
}
// Transition in and out the Thumbnail HUD element
export class ShowThumbnail extends Event {
    start() {
        super.start();
        this.zeroTime = new Date().getTime();
        this.duration = this.options.duration;
        this.exiting = false;
    }
    update() {
        // T increases from 0->1 while transitioning in, and 1->0 while transitioning out
        const t = (new Date().getTime() - this.zeroTime) / this.duration;
        // Set HUD parameters based on t
        this.options.thumbnail.setScale(this.exiting ? Ease.inOutCubic(t) : Ease.outElastic(t));
        this.options.thumbnail.setOpacity(Ease.outCubic(t));
        this.options.thumbnail.setShow(t > 0.0);
        // When the Thumbnail is not shown, show the Dot
        this.options.dot.setOpacity(1.0 - Ease.outCubic(t));
        this.options.dot.setShow(this.options.dot.opacity > 0.0);
        // Stop updating once we've completely transitioned (in or out)
        if (t < 0.0 || t > 1.0)
            this.stop();
    }
    undo() {
        super.start();
        // Set the zero time in the future so that t will decrease
        this.zeroTime = new Date().getTime() + this.options.duration;
        this.duration = -this.options.duration;
        this.exiting = true;
    }
}
export default class EventCollection {
    constructor() {
        this.triggers = [];
        this.events = [];
    }
    update(app) {
        // Evaluate all triggers
        this.triggers.forEach(t => {
            if (t)
                t.update(app);
        });
        // Update all active events
        this.events.forEach(e => {
            if (e.active) {
                e.update(app);
            }
        });
    }
    add(event, triggerName, options) {
        if (!event)
            throw new Error('Invalid Event:', event);
        // If a trigger is not specified, this event must be started manually
        const trigger = triggerName ? new Trigger(triggerName, event, options) : null;
        this.events.push(event);
        this.triggers.push(trigger);
    }
    remove(event) {
        const idx = this.events.indexOf(event);
        if (idx < 0)
            throw new Error('Attempted to remove Event that is not registered');
        // @TODO: Test the case of removing a media item while it's active (e.g. during FocusOnPhoto)
        // if (event.active) { event.undo(); }
        this.events.splice(idx, 1);
        this.triggers.splice(idx, 1);
    }
}
